import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

interface NetworkLog {
  id: string;
  timestamp: Date;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  requestHeaders?: Record<string, string>;
  requestBody?: any;
  responseHeaders?: Record<string, string>;
  responseBody?: any;
  error?: string;
}

class NetworkDebugger {
  private logs: NetworkLog[] = [];
  private enabled: boolean = false;
  private maxLogs: number = 100;
  private logToConsole: boolean = true;

  constructor() {
    // Enable in development
    this.enabled = import.meta.env.DEV;
    
    // Make available in window for debugging
    if (typeof window !== 'undefined') {
      (window as any).networkDebugger = this;
    }
  }

  enable() {
    this.enabled = true;
    this.setupInterceptors();
    console.log('🔍 Network debugger enabled');
  }

  disable() {
    this.enabled = false;
    console.log('🔍 Network debugger disabled');
  }

  private setupInterceptors() {
    // Request interceptor
    axios.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        if (!this.enabled) return config;

        const log: NetworkLog = {
          id: this.generateId(),
          timestamp: new Date(),
          method: config.method?.toUpperCase() || 'GET',
          url: config.url || '',
          requestHeaders: config.headers as Record<string, string>,
          requestBody: config.data
        };

        this.addLog(log);

        // Add request start time for duration calculation
        (config as any).metadata = { startTime: Date.now(), logId: log.id };

        if (this.logToConsole) {
          console.group(`🔷 ${log.method} ${log.url}`);
          console.log('Headers:', log.requestHeaders);
          if (log.requestBody) {
            console.log('Body:', log.requestBody);
          }
          console.groupEnd();
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    axios.interceptors.response.use(
      (response: AxiosResponse) => {
        if (!this.enabled) return response;

        const metadata = (response.config as any).metadata;
        if (metadata) {
          const duration = Date.now() - metadata.startTime;
          this.updateLog(metadata.logId, {
            status: response.status,
            duration,
            responseHeaders: response.headers as Record<string, string>,
            responseBody: response.data
          });

          if (this.logToConsole) {
            console.group(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status} (${duration}ms)`);
            console.log('Response:', response.data);
            console.groupEnd();
          }
        }

        return response;
      },
      (error: AxiosError) => {
        if (!this.enabled) return Promise.reject(error);

        const metadata = (error.config as any)?.metadata;
        if (metadata) {
          const duration = Date.now() - metadata.startTime;
          this.updateLog(metadata.logId, {
            status: error.response?.status,
            duration,
            error: error.message,
            responseBody: error.response?.data
          });

          if (this.logToConsole) {
            console.group(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || 'FAILED'} (${duration}ms)`);
            console.error('Error:', error.message);
            if (error.response?.data) {
              console.error('Response:', error.response.data);
            }
            console.groupEnd();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private addLog(log: NetworkLog) {
    this.logs.unshift(log);
    
    // Keep only maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }
  }

  private updateLog(id: string, updates: Partial<NetworkLog>) {
    const index = this.logs.findIndex(log => log.id === id);
    if (index !== -1) {
      this.logs[index] = { ...this.logs[index], ...updates };
    }
  }

  // Public methods for debugging

  getLogs(): NetworkLog[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    console.log('🔍 Network logs cleared');
  }

  filterLogs(predicate: (log: NetworkLog) => boolean): NetworkLog[] {
    return this.logs.filter(predicate);
  }

  getFailedRequests(): NetworkLog[] {
    return this.logs.filter(log => log.error || (log.status && log.status >= 400));
  }

  getSlowRequests(thresholdMs: number = 1000): NetworkLog[] {
    return this.logs.filter(log => log.duration && log.duration > thresholdMs);
  }

  getRequestsByEndpoint(endpoint: string): NetworkLog[] {
    return this.logs.filter(log => log.url.includes(endpoint));
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  downloadLogs() {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `network-logs-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Utility methods for analysis

  getAverageResponseTime(endpoint?: string): number {
    const relevantLogs = endpoint 
      ? this.logs.filter(log => log.url.includes(endpoint) && log.duration)
      : this.logs.filter(log => log.duration);
    
    if (relevantLogs.length === 0) return 0;
    
    const totalDuration = relevantLogs.reduce((sum, log) => sum + (log.duration || 0), 0);
    return Math.round(totalDuration / relevantLogs.length);
  }

  getEndpointStats() {
    const stats: Record<string, {
      count: number;
      avgDuration: number;
      successRate: number;
      methods: Set<string>;
    }> = {};

    this.logs.forEach(log => {
      const endpoint = this.extractEndpoint(log.url);
      if (!stats[endpoint]) {
        stats[endpoint] = {
          count: 0,
          avgDuration: 0,
          successRate: 0,
          methods: new Set()
        };
      }

      stats[endpoint].count++;
      stats[endpoint].methods.add(log.method);
      
      if (log.duration) {
        stats[endpoint].avgDuration = 
          (stats[endpoint].avgDuration * (stats[endpoint].count - 1) + log.duration) / 
          stats[endpoint].count;
      }

      if (log.status && log.status < 400) {
        stats[endpoint].successRate = 
          (stats[endpoint].successRate * (stats[endpoint].count - 1) + 1) / 
          stats[endpoint].count;
      }
    });

    return stats;
  }

  private extractEndpoint(url: string): string {
    try {
      const urlObj = new URL(url, window.location.origin);
      // Remove query params and dynamic segments
      return urlObj.pathname
        .replace(/\/\d+/g, '/:id')
        .replace(/\/[a-f0-9-]{36}/g, '/:uuid');
    } catch {
      return url;
    }
  }

  // Console helpers
  printSummary() {
    console.group('🔍 Network Summary');
    console.log(`Total Requests: ${this.logs.length}`);
    console.log(`Failed Requests: ${this.getFailedRequests().length}`);
    console.log(`Average Response Time: ${this.getAverageResponseTime()}ms`);
    
    const stats = this.getEndpointStats();
    console.table(
      Object.entries(stats).map(([endpoint, data]) => ({
        Endpoint: endpoint,
        Count: data.count,
        'Avg Duration (ms)': Math.round(data.avgDuration),
        'Success Rate': `${Math.round(data.successRate * 100)}%`,
        Methods: Array.from(data.methods).join(', ')
      }))
    );
    
    console.groupEnd();
  }
}

// Create singleton instance
export const networkDebugger = new NetworkDebugger();

// Export types
export type { NetworkLog };

// Console API for easy debugging
if (typeof window !== 'undefined') {
  (window as any).nd = {
    enable: () => networkDebugger.enable(),
    disable: () => networkDebugger.disable(),
    logs: () => networkDebugger.getLogs(),
    clear: () => networkDebugger.clearLogs(),
    failed: () => networkDebugger.getFailedRequests(),
    slow: (ms?: number) => networkDebugger.getSlowRequests(ms),
    summary: () => networkDebugger.printSummary(),
    download: () => networkDebugger.downloadLogs()
  };
}